import { getCsrfToken } from "@/api/messages";
import type { ChatConversation, ChatMessage } from "@/types/chat";

export const OPPORTUNITY_RESEARCH_CHAT_KIND = "opportunity_research";
export const OPPORTUNITY_RESEARCH_POLL_INTERVAL_MS = 2000;
// Package RAG runs are comparable to municipality RA latency; match its 4-minute ceiling.
export const OPPORTUNITY_RESEARCH_POLL_MAX_ATTEMPTS = 120;

/**
 * The API deliberately answers 404 (not 403) for an opportunity chat the caller
 * cannot see, so "missing" and "forbidden" are indistinguishable by design.
 */
export class OpportunityResearchUnavailableError extends Error {
  constructor() {
    super("The Research Assistant isn't available for this opportunity.");
    this.name = "OpportunityResearchUnavailableError";
  }
}

async function jsonOrThrow<T>(response: Response, failureMessage: string): Promise<T> {
  if (response.status === 404) throw new OpportunityResearchUnavailableError();
  if (!response.ok) throw new Error(failureMessage);
  return response.json() as Promise<T>;
}

export function isOpportunityResearchChat(
  chat: ChatConversation,
  opportunityId: number,
): boolean {
  return (
    chat.kind === OPPORTUNITY_RESEARCH_CHAT_KIND &&
    chat.grant_opportunity_id === opportunityId &&
    !chat.discarded_at
  );
}

/** Newest first, matching the index ordering; returns null when the user has no chat yet. */
export async function findLatestOpportunityResearchChat(
  opportunityId: number,
): Promise<ChatConversation | null> {
  const response = await fetch("/api/v1/chats");
  const chats = await jsonOrThrow<ChatConversation[]>(response, "Could not load conversations.");
  return chats.find((chat) => isOpportunityResearchChat(chat, opportunityId)) ?? null;
}

export async function createOpportunityResearchChat(
  municipalityId: number,
  opportunityId: number,
): Promise<ChatConversation> {
  const response = await fetch("/api/v1/chats", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      chat: {
        kind: OPPORTUNITY_RESEARCH_CHAT_KIND,
        municipality_id: municipalityId,
        grant_opportunity_id: opportunityId,
      },
    }),
  });
  return jsonOrThrow<ChatConversation>(response, "Could not start the conversation.");
}

export async function getOpportunityResearchChat(chatId: number): Promise<ChatConversation> {
  const response = await fetch(`/api/v1/chats/${chatId}`);
  return jsonOrThrow<ChatConversation>(response, "Could not load the conversation.");
}

/**
 * No selected_document_ids: the opportunity mode's document scope is the
 * opportunity package itself and the server ignores per-message selections.
 */
export async function postOpportunityResearchMessage(
  chatId: number,
  content: string,
): Promise<ChatMessage> {
  const response = await fetch("/api/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      chat_id: chatId,
      message: {
        content,
        source: "from_human",
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
  });
  return jsonOrThrow<ChatMessage>(response, "Could not send your message.");
}

/** "New conversation" archives the old thread so a later mount doesn't resume it. */
export async function archiveOpportunityResearchChat(chatId: number): Promise<void> {
  const response = await fetch("/api/v1/chats/archive", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ id: chatId }),
  });
  await jsonOrThrow<unknown>(response, "Could not archive the conversation.");
}

/**
 * Every bot message the server writes — RAG answers, refusals, moderation
 * replies — is stamped with the question it answers, so match on that rather
 * than on ordering. Ordering is wrong after a poll timeout: the user asks
 * again, the slow first answer lands, and by "newest bot message" it looks
 * like the reply to the second question.
 */
export function hasBotReplyTo(chat: ChatConversation, question: ChatMessage): boolean {
  return (chat.messages ?? []).some(
    (message) => message.source === "from_bot" && message.response_to_message_id === question.id,
  );
}
